import { Injectable } from "@angular/core";
import { SortByAttribute } from "@hsgovtech/hsutils";
import { FormGroup } from "@angular/forms";
import { IrefCategoriesRepository } from "../repositories/iref.category.repository";
import { InspectionViolationRepository } from "../repositories/inspection.violation.repository";
import { ViolationCategory } from "../types/Violation/ViolationCategory";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { ObservationService } from "./observation.service";
import { IrefItemCodeRepository } from "../repositories/iref.item.code.repository";
import { IrefItemRepository } from "../repositories/iref.item.repository";
import { LocationHistory } from "../types/Location/LocationHistory";
import { FormService } from "./form.service";
import { Inspection } from "../types/Inspection/Inspection";
import { FollowUpConfigs } from "../types/Inspection/FollowUpConfigs";
import { ViolationItem } from "../types/Violation/ViolationItem";

@Injectable({
  providedIn: "root",
})
export class CategoryService extends BaseService {
  constructor(
    private irefItemRepo: IrefItemRepository,
    private irefItemCodeRepo: IrefItemCodeRepository,
    private inspectionViolationRepo: InspectionViolationRepository,
    private irefCategoryRepo: IrefCategoriesRepository,
    private observationService: ObservationService,
    private formService: FormService,
    logService: LogService,
  ) {
    super(logService, "CategoryService");
  }

  public getIrefCategoriesByInspectionTypeID(
    InspectionTypeID: string,
  ): ViolationCategory[] {
    return this.irefCategoryRepo.getIrefCategoriesByInspectionTypeID(
      InspectionTypeID,
    );
  }

  private isDisabledViolationItem(item: ViolationItem): boolean {
    const disabled = String(
      (item as ViolationItem & { disabled?: any }).disabled ?? "0",
    )
      .trim()
      .toLowerCase();
    return disabled === "1" || disabled === "true" || disabled === "yes";
  }

  public getPriorViolationFilters(
    followUpConfigs: FollowUpConfigs,
    followUpID: string,
    history: LocationHistory,
    items: ViolationItem[],
    categories: ViolationCategory[],
  ) {
    // If this inspection type is a follow up and they only want to see the prior violations only, then do so now

    if (followUpConfigs && followUpID && followUpConfigs.onlyShowPrior) {
      const observationsFromPreviousInspections: any =
        this.observationService.getObservationsFromAllPreviousInspections(
          history,
          followUpConfigs.copyOnlyViolations,
        );

      const addedCategoryIDs = new Set(); // Track category IDs already added

      return Object.entries(observationsFromPreviousInspections).reduce(
        (acc, [historyKey, historyItem]: [string, any]) => {
          const inspItem = items.find(
            (item: ViolationItem) => item.itemID === historyKey,
          );
          if (inspItem) {
            const category = categories.find(
              (cat: ViolationCategory) =>
                cat.categoryID === inspItem.categoryID,
            );
            if (category) {
              const isViolationInFollowUp = historyItem.some(
                (violation: any) =>
                  violation.inspectionID === followUpID ||
                  violation.inspectionId === followUpID,
              );
              if (isViolationInFollowUp) {
                acc.filteredItems.push(inspItem);
                // Add category only if it's not already in the Set
                if (!addedCategoryIDs.has(category.categoryID)) {
                  acc.filteredCategories.push(category);
                  addedCategoryIDs.add(category.categoryID);
                }
              }
            }
          }
          return acc;
        },
        {
          filteredItems: [] as any,
          filteredCategories: [] as ViolationCategory[],
        },
      );
    }
    return { filteredCategories: [], filteredItems: [] };
  }

  // Iterates through categories and items for this inspection type and adds violations, observations, etc. to a global object
  public indexViolationEntryData(
    inspection: Inspection,
    categories: ViolationCategory[],
    followUpConfigs: FollowUpConfigs,
    history: LocationHistory,
  ) {
    const violations =
      this.inspectionViolationRepo.getInspectionViolationsByInspectionID(
        inspection.locationID,
        inspection.inspectionID,
      );
    const items = this.irefItemRepo
      .get()
      .filter((item) => !this.isDisabledViolationItem(item));
    const observations = this.observationService.getObservations();
    const violationItemCodes = this.irefItemCodeRepo.get();
    const { filteredCategories } = this.getPriorViolationFilters(
      followUpConfigs,
      inspection.followUpID,
      history,
      items,
      categories,
    );

    const categoriesToShow =
      filteredCategories && filteredCategories.length
        ? filteredCategories
        : categories;

    const observationsFromPreviousInspections =
      this.observationService.getObservationsFromAllPreviousInspections(
        history || {},
        true,
      );
    return categoriesToShow.map((cat: any) => {
      const filteredItems = items.filter(
        (item: any) => item.categoryID === cat.categoryID,
      );
      const sortedItems = SortByAttribute(filteredItems, "sort");
      const required = this.getRequired(
        inspection.inspectionTypeID,
        filteredItems,
      );
      const hasViolations = this.getRemainingViolationItemsMap(
        filteredItems,
        observationsFromPreviousInspections,
      );
      return {
        ...cat,
        visited: this.getVisitedViolationItems(filteredItems),
        hasViolations,
        hasViolationsArr: Object.values(hasViolations),
        required,
        requiredArr: Object.values(required),
        items: this.observationService.getObservationInfo(
          sortedItems,
          violations,
          observations,
          violationItemCodes,
          filteredItems,
        ),
      };
    });
  }

  public getCategories(
    inspection: Inspection,
    followUpConfigs: any,
    history: any,
  ): ViolationCategory[] {
    this.observationService.setObservationMap(
      inspection.locationID,
      inspection.inspectionID,
    );
    const categories = this.getIrefCategoriesByInspectionTypeID(
      inspection.inspectionTypeID,
    );
    const sortedCategories = SortByAttribute(categories, "sort");
    const res = this.indexViolationEntryData(
      inspection,
      sortedCategories,
      followUpConfigs,
      history,
    );
    return res;
  }

  public violationItemIsRequired(
    generalInfoForm: FormGroup,
    inspectionType: any,
    item: ViolationItem,
  ) {
    const isRequired = (item.required || "").toString().toLowerCase() === "yes";
    const requirementExceptions = this.getRequirementExceptions(item);
    if (
      isRequired &&
      requirementExceptions &&
      Object.keys(requirementExceptions).length
    ) {
      return Object.keys(requirementExceptions).every((field: string) => {
        const exceptions = requirementExceptions[field];
        if (!Array.isArray(exceptions) || exceptions.length === 0) {
          return true;
        }
        const formValue =
          generalInfoForm?.get(field)?.value ?? inspectionType[field];
        return !exceptions.includes(formValue);
      });
    }
    return isRequired;
  }

  public getVisitedViolationItems(items: ViolationItem[]) {
    return items.reduce((acc: any, item: ViolationItem) => {
      if (this.observationService.getObservationFromMap(item.itemID)) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }

  public getRemainingViolationItemsMap(
    items: ViolationItem[],
    observations: any,
  ) {
    return items.reduce((acc: any, item: ViolationItem) => {
      if (this.itemHasViolations(item, observations)) {
        if (!this.observationService.getObservationFromMap(item.itemID)) {
          return { ...acc, [item.itemID]: item };
        }
      }
      return acc;
    }, {});
  }

  public categoryHasViolations(items: ViolationItem[], history: any) {
    const observations =
      this.observationService.getObservationsFromAllPreviousInspections(
        history || {},
        true,
      );
    if (items) {
      return items.some(
        (item: ViolationItem) => observations[item.itemID]?.length,
      );
    }
    return false;
  }

  public itemHasViolations(item: ViolationItem, observations: any) {
    return observations[item.itemID]?.length;
  }

  public getRequirementExceptions(item: ViolationItem) {
    if (
      item.requirementExceptions &&
      item.requirementExceptions.trim().length &&
      item.requirementExceptions !== "{}"
    ) {
      return JSON.parse(item.requirementExceptions);
    }
    return undefined;
  }

  public calculateNumberOfCompletedItems(categories: any) {
    const itemsLength = categories.reduce((acc: any, item: any) => {
      return acc + item.items.length;
    }, 0);
    const requiredItemsLength = categories.reduce((acc: any, item: any) => {
      return acc + Object.keys(item.required).length;
    }, 0);
    const violationItemsLength = categories.reduce((acc: any, item: any) => {
      return acc + Object.keys(item.hasViolations).length;
    }, 0);
    const completedItemsLength = categories.reduce(
      (acc: any, category: any) => {
        return (
          acc +
          category.items.filter((item: any) => {
            return item.completed;
          }).length
        );
      },
      0,
    );
    return {
      itemsLength,
      completedItemsLength,
      requiredItemsLength,
      violationItemsLength,
    };
  }

  public getRequired(inspectionTypeID: string, items: any) {
    const generalInfoForm = this.formService.getForm("generalInfo");
    return items.reduce((acc: any, item: any) => {
      if (
        this.violationItemIsRequired(
          generalInfoForm.state,
          inspectionTypeID,
          item,
        )
      ) {
        if (!this.observationService.getObservationFromMap(item.itemID)) {
          return { ...acc, [item.itemID]: item };
        }
      }
      return acc;
    }, {});
  }
}
